import { ReactElement, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { handlePreventDefault, isFilterValuePresent } from '@helper/utils';
import { useFilterStore } from '@store/filter';
import { useSiteListFilterStore } from '@store/lms/siteListFilterStore';
import { useCompetitionFilterStore } from '@store/lms/competitionFilterStore';
import { ISiteListSearchProps } from '.';

export const SiteListSearch = (props: ISiteListSearchProps): ReactElement => {
	const { setFilterOpen, setSortOpen, placeHolder } = props;
	const location = useLocation();
	const { search, updateSearch, updateSortObject, updateSortValue, updateSortActiveStatus, sortActiveStatus } =
		useFilterStore((state) => state);
	const { siteListFilter: allSiteFilter } = useSiteListFilterStore();
	const { competitionFilter, upcomingFilter } = useCompetitionFilterStore();

	const isUpcomingRoute = location.pathname === '/lms/sites/upcoming';
	const isCompetitionRoute = location.pathname === '/lms/sites/competition';

	// Get the appropriate filter based on route
	const currentFilter = useMemo(() => {
		if (isCompetitionRoute) {
			return competitionFilter;
		}
		if (isUpcomingRoute) {
			return upcomingFilter;
		}
		return allSiteFilter;
	}, [isCompetitionRoute, isUpcomingRoute, competitionFilter, upcomingFilter, allSiteFilter]);

	const isFilterActive: boolean = useMemo(() => {
		return Object.entries(currentFilter).some(([key, value]) => {
			const shouldShowFilter =
				key === 'location'
					? value && typeof value === 'object' && value.name && value.name.length > 0
					: isFilterValuePresent(value);

			const shouldHideStatusFilter = key === 'site_current_status_c' && value === 'Upcoming';
			const shouldHideSolventFilter = key === 'solvent_used_c' && value === 'Competition';

			return shouldShowFilter && !shouldHideStatusFilter && !shouldHideSolventFilter;
		});
	}, [currentFilter]);

	useEffect(() => {
		let sortOptions;

		if (isUpcomingRoute) {
			// Sort options for upcoming route
			sortOptions = [
				{ value: 'fevicol_business_potential_c', name: 'Glue Potential' },
				{ value: 'priority', name: 'Priority Order of Site' },
				{ value: 'date_of_next_visit', name: 'Site Start Date' },
			];
		} else {
			// Default sort options for other routes
			sortOptions = [
				{ value: 'priority', name: 'Priority Order' },
				{ value: 'fevicol_business_potential_c', name: 'Glue Potential' },
				{ value: 'epoxy_adhesive_used_c', name: 'Conversion Potential' },
				{ value: 'createddate', name: 'Created Date' },
			];
		}

		updateSortObject(sortOptions);
		updateSortValue('');
		updateSortActiveStatus(false);
		updateSearch('');

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isUpcomingRoute]);

	return (
		<div className="filter-section">
			<div className="search-filter-wrapper">
				<div className="search-with-filter">
					<div className="search">
						<form onSubmit={handlePreventDefault}>
							<div className="search-row">
								<div className="input-col">
									<label htmlFor="search-top" className="hide">
										search
									</label>
									<input
										value={search}
										onChange={(e) => updateSearch(e.target.value)}
										type="text"
										name="search"
										id="search-top"
										placeholder={placeHolder}
										className="search-bar"
										autoComplete="off"
									/>
								</div>

								<button className="search-button" aria-label="search user" onClick={handlePreventDefault}>
									<span className="icon-search"></span>
								</button>
							</div>
						</form>
					</div>

					<div className="filters">
						<button
							className={`filter-button ${sortActiveStatus && 'active'}`}
							aria-label="filter for user"
							id="sort-lead-btn"
							onClick={setSortOpen}
						>
							<span className="icon-slider"></span>
						</button>

						<button
							className={`filter-button ${isFilterActive && 'active'}`}
							aria-label="filter"
							id="filter-contractor"
							onClick={setFilterOpen}
						>
							<span className="icon-filter"></span>
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};
